import React from 'react';
import { Box, Paper, Typography, LinearProgress, Chip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';
import GroupIcon from '@mui/icons-material/Group';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
// Helper functions for card styling

// Function to find card colors by title from swimlaneData
const getCardColorByTitle = (title, swimlaneData) => {
  if (!title || !swimlaneData) return { borderColor: '#1976d2', backgroundColor: 'rgba(25, 118, 210, 0.1)', cardId: null };
  
  // Define known color mappings for specific cards
  const colorMap = {
    'CIR': { borderColor: 'red', backgroundColor: 'rgba(211, 47, 47, 0.1)', cardId: 'csr' }, // Example, ensure IDs are correct
    'DIR': { borderColor: 'grey', backgroundColor: 'rgba(0, 0, 0, 0.05)', cardId: 'dir' }, // Example
    'Project Charter': { borderColor: '#1976d2', backgroundColor: 'rgba(25, 118, 210, 0.1)', cardId: 'project-charter' }, // Example
    'DDP': { borderColor: '#1976d2', backgroundColor: 'rgba(25, 118, 210, 0.1)', cardId: 'prd' }, // Example
    'CHA': { borderColor: 'black', backgroundColor: 'white', cardId: null }, // No corresponding card
    'SAD': { borderColor: 'black', backgroundColor: 'white', cardId: null } // No corresponding card
  };
  
  // First check if we have a direct mapping
  if (colorMap[title]) {
    return colorMap[title];
  }
  
  // If not in the map, search through all swimlanes and cards
  for (const lane of swimlaneData) {
    for (const cardItem of lane.cards) { // Renamed 'card' to 'cardItem' to avoid conflict with outer 'card' prop
      // Check if this card's title matches the dependency/output name
      // or if the card's ID matches (some dependencies might reference by ID)
      if (cardItem.title === title || cardItem.id === title.toLowerCase()) {
        // Ensure we have valid color values
        const borderColor = cardItem.borderColor || '#1976d2';
        let bgColor;
        
        // Create appropriate background colors based on border colors
        if (borderColor === 'red') {
          bgColor = 'rgba(211, 47, 47, 0.1)';
        } else if (borderColor === 'grey') {
          bgColor = 'rgba(0, 0, 0, 0.05)';
        } else if (borderColor === '#1976d2') {
          bgColor = 'rgba(25, 118, 210, 0.1)';
        } else {
          // Default background with some opacity for any other color
          // Ensure borderColor is a hex color if appending opacity, e.g., if it's a named color, this will break.
          // For safety, let's use a default if borderColor is not a typical hex.
          const defaultBgColor = 'rgba(0,0,0,0.05)'; // A neutral default
          try {
            bgColor = borderColor.startsWith('#') ? `${borderColor}20` : defaultBgColor;
          } catch (e) {
            bgColor = defaultBgColor;
          }
        }
        
        return {
          borderColor: borderColor,
          backgroundColor: bgColor,
          cardId: cardItem.id // Ensure cardId is returned
        };
      }
    }
  }
  
  // If not found, return default colors
  return { borderColor: '#1976d2', backgroundColor: 'rgba(25, 118, 210, 0.1)', cardId: null };
};

const WorkflowCard = ({
  card,
  cardRef,
  onMouseEnter,
  onMouseLeave,
  isHighlighted,
  hoveredLinkedCardId,
  swimlaneData,
  hoveredChipTargetCardId,
  setHoveredChipTargetCardId,
  highlightedDependency,
  setHighlightedDependency
}) => {
  // Default fallback for cards not found
  
  // No debugging code needed
  
  // Extract completion criteria details
  const criteriaText = card.completionCriteria || '';
  const docMatch = criteriaText.match(/(\d+)\s*Documents?/);
  const reviewMatch = criteriaText.match(/(\d+)\s*Reviews?/);
  
  const docText = docMatch ? `${docMatch[1]} Documents` : '';
  const reviewText = reviewMatch ? `${reviewMatch[1]} Reviews` : '';
  
  // Determine card styling based on state and variant
  const isDirectlyHovered = hoveredLinkedCardId === card.id;
  const isChipTargetHovered = hoveredChipTargetCardId === card.id;
  const cardBorderColor = card.borderColor || 'grey';
  const cardVariant = card.variant || 'Default';
  
  const getStatusColor = () => {
    if (cardVariant === 'Completed') return '#4caf50';
    return 'grey.500';
  };
  
  return (
    <Paper
      ref={cardRef}
      elevation={3}
      sx={{
        p: '0.5rem 0.5rem 0.5rem 0.5rem', // Reduced top padding
        borderLeft: `5px solid ${cardBorderColor}`,
        borderColor: cardBorderColor,
        border: isDirectlyHovered || isChipTargetHovered || isHighlighted ? `2px solid ${cardBorderColor}` : `1px solid ${cardBorderColor}30`,
        borderLeftWidth: '5px',
        backgroundColor: '#fff', // Keep background opaque on hover/highlight
        transition: 'all 0.2s',
        transform: isDirectlyHovered || isChipTargetHovered || isHighlighted ? 'scale(1.02)' : 'scale(1)',
        width: '280px',
        minWidth: '280px',
        height: 'fit-content',
        position: 'relative',
        zIndex: 2,
        '&:hover': {
          zIndex: 20,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          cursor: 'pointer'
        },
      }}
      onMouseEnter={() => onMouseEnter && onMouseEnter(card.id)}
      onMouseLeave={() => onMouseLeave && onMouseLeave()}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
          {card.title}
        </Typography>
        <Box 
          sx={{ 
            width: 18, 
            height: 18, 
            borderRadius: '50%', 
            backgroundColor: getStatusColor(),
            opacity: cardVariant === 'Completed' ? 1 : 0.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {cardVariant === 'Completed' && (
            <Box component="span" sx={{ color: '#fff', fontSize: '0.8rem' }}>✓</Box>
          )}
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountCircleIcon sx={{ fontSize: '0.95rem', color: 'text.secondary', mr: 0.5 }} />
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            {card.owner}
          </Typography>
        </Box>
        
        {card.dueDate && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon sx={{ fontSize: '0.95rem', color: 'text.secondary', mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {card.dueDate}
            </Typography>
          </Box>
        )}
      </Box>
      
      <Box sx={{ mt: 1.5, mb: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Progress
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {card.progress}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={card.progress} 
          sx={{
            height: 5,
            borderRadius: 5,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: card.progress === 100 ? '#4caf50' : cardBorderColor,
            },
          }}
        />
      </Box>
      
      <Box sx={{ mt: 1.5 }}>
        {/* Dependencies section */}
        {card.dependencies && card.dependencies.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 'medium', mr: 1 }}>
                Dependencies:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {card.dependencies.map((dep, index) => {
                  const depColors = getCardColorByTitle(dep, swimlaneData);
                  const depCardInfo = getCardColorByTitle(dep, swimlaneData);
                  return (
                    <Chip
                      key={index}
                      label={dep}
                      size="small"
                      variant="outlined"
                      onMouseEnter={() => {
                        if (depCardInfo && depCardInfo.cardId) {
                          setHoveredChipTargetCardId(depCardInfo.cardId);
                          // Set the highlighted dependency relationship
                          setHighlightedDependency({
                            from: depCardInfo.cardId,
                            to: card.id
                          });
                        }
                      }}
                      onMouseLeave={() => {
                        setHoveredChipTargetCardId(null);
                        setHighlightedDependency(null);
                      }}
                      sx={{ 
                        height: 20, 
                        fontSize: '0.65rem',
                        borderColor: depColors.borderColor,
                        backgroundColor: depColors.backgroundColor,
                        color: 'text.primary',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderWidth: 2,
                          cursor: 'pointer'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
        
        {/* Outputs section */}
        {card.outputs && card.outputs.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 'medium', mr: 1 }}>
                Outputs:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {card.outputs.map((output, index) => {
                  const outputColors = getCardColorByTitle(output, swimlaneData);
                  const outputCardInfo = getCardColorByTitle(output, swimlaneData);
                  return (
                    <Chip
                      key={index}
                      label={output}
                      size="small"
                      variant="outlined"
                      onMouseEnter={() => {
                        if (outputCardInfo && outputCardInfo.cardId) {
                          setHoveredChipTargetCardId(outputCardInfo.cardId);
                          // Set the highlighted dependency relationship
                          setHighlightedDependency({
                            from: card.id,
                            to: outputCardInfo.cardId
                          });
                        }
                      }}
                      onMouseLeave={() => {
                        setHoveredChipTargetCardId(null);
                        setHighlightedDependency(null);
                      }}
                      sx={{ 
                        height: 20, 
                        fontSize: '0.65rem',
                        borderColor: outputColors.borderColor,
                        backgroundColor: outputColors.backgroundColor,
                        color: 'text.primary',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderWidth: 2,
                          cursor: 'pointer'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
        
        {/* Completion criteria section */}
        {(docText || reviewText) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {docText && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArticleIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {docText}
                </Typography>
              </Box>
            )}
            {reviewText && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {reviewText}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default WorkflowCard;
